import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { LANDING } from '@/constants/testIds';
import { ASSETS } from '@/lib/assets';

/**
 * Lifestyle Gallery — horizontal-scroll editorial reveal of the bottle in
 * the wild, using KIVO's own product photography.
 */
const slides = [
  {
    img: ASSETS.heroBlackMarble,
    label: 'The Studio',
    place: 'Matte Black · Corporate Chaos',
    quote: 'Sip first. Panic later.',
  },
  {
    img: ASSETS.heroStoneGreyDesk,
    label: 'The Morning Desk',
    place: 'Stone Grey · 7:24 a.m.',
    quote: 'Your 4th coffee doesn’t count.',
  },
  {
    img: ASSETS.heroNavySkyline,
    label: 'The Skyline Office',
    place: 'Navy · Mid-week meeting',
    quote: 'Meeting survived. Hydrate.',
  },
  {
    img: ASSETS.girlsClubLifestyle,
    label: 'The Living Room',
    place: 'Pink · Slow Sunday',
    quote: 'Drink water, bestie.',
  },
  {
    img: ASSETS.genzOfficeIndia,
    label: 'The Bombay Office',
    place: 'Yellow · Gen-Z floor',
    quote: 'Oye. Paani pee le.',
  },
  {
    img: ASSETS.sageGreenDesk,
    label: 'The Reading Nook',
    place: 'Sage Green · Slow afternoon',
    quote: 'Paani piya kya?',
  },
];

export default function LifestyleGallery() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const x = useTransform(scrollYProgress, [0, 1], ['0%', '-72%']);

  return (
    <section
      id="lifestyle"
      ref={ref}
      data-testid={LANDING.lifestyleGallery}
      className="relative bg-[#FAF8F4]"
      style={{ height: '320vh' }}
    >
      <div className="sticky top-0 h-[100svh] flex flex-col overflow-hidden">
        <div className="px-6 lg:px-12 pt-28">
          <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
            <div>
              <div className="text-[11px] tracking-[0.35em] uppercase text-[#CBBBA0] mb-4">
                Chapter 06 · A Life Lived Well
              </div>
              <h2 className="font-display text-[clamp(2.5rem,6vw,5rem)] leading-[0.95] tracking-[-0.03em] text-[#111]">
                The world,<br />
                <span className="font-serif italic text-[#111]/70">with KIVO in it.</span>
              </h2>
            </div>
            <div className="text-[11px] tracking-[0.25em] uppercase text-[#111]/50">
              ← Scroll to journey →
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center mt-10 lg:mt-16">
          <motion.div
            style={{ x }}
            className="flex gap-6 lg:gap-10 pl-6 lg:pl-12 will-change-transform"
          >
            {slides.map((s, i) => (
              <div
                key={s.label}
                className="relative shrink-0 w-[78vw] sm:w-[60vw] lg:w-[46vw] h-[62vh] rounded-3xl overflow-hidden bg-[#1A1A1A]"
              >
                <img
                  src={s.img}
                  alt={s.label}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />

                {/* Index */}
                <div className="absolute top-6 left-6 text-[10px] tracking-[0.3em] uppercase text-white/70">
                  0{i + 1} / 0{slides.length}
                </div>

                {/* Quote ribbon (top-right) */}
                <div className="absolute top-6 right-6 text-right text-white/80 max-w-[60%]">
                  <div className="font-serif italic text-base leading-tight">{s.quote}</div>
                </div>

                {/* Caption */}
                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-white">
                  <div>
                    <div className="font-display text-2xl tracking-tight">{s.label}</div>
                    <div className="text-[11px] tracking-[0.25em] uppercase text-white/70 mt-1">
                      {s.place}
                    </div>
                  </div>
                  <div className="font-serif italic text-[#CBBBA0] text-lg">KIVO</div>
                </div>
              </div>
            ))}
            <div className="shrink-0 w-[20vw]" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
